import {useContext, useEffect, useRef} from 'react';
import {debounce} from 'lodash';

import {useContentDiscoveryStore} from '../ContentDiscoveryStore.react';
import {ContentDiscoveryContext} from '../../ContentDiscoveryContext';

export const useTopicsModalSubjectsSubscription = () => {
  const standardCollection = useContentDiscoveryStore(
    (state) => state.standards.standardCollection
  );
  const getSelectedStandardsAndSets = useContentDiscoveryStore(
    (state) => state.standards.getSelectedStandardsAndSets
  );

  const {
    questionDifficulties,
    questionTypes,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    isBrowsingBeyondAccess
  } = useContext(ContentDiscoveryContext);

  const {selectedStandardIds, selectedStandardSetIds} =
    getSelectedStandardsAndSets(standardCollection);

  const selectedContentDiscoveryFilters = {
    standardIds: selectedStandardIds,
    standardSetIds: selectedStandardSetIds,
    questionDifficulties,
    questionTypes,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    filterByUserAccess: !isBrowsingBeyondAccess
  };

  // The effect below subscribes once (empty deps),
  // so its callbacks must not close over the first render's filter snapshot.
  // Keep the latest filters in a ref so subscribe callbacks and init fetches read
  // fresh values after a filter/context change.
  const filtersRef = useRef(selectedContentDiscoveryFilters);
  useEffect(() => {
    filtersRef.current = selectedContentDiscoveryFilters;
  });

  // The license segment can resolve after the modal opens, flipping
  // isBrowsingBeyondAccess; refetch so counts match the new access scope.
  const previousBrowsingBeyondAccessRef = useRef(isBrowsingBeyondAccess);
  useEffect(() => {
    if (previousBrowsingBeyondAccessRef.current === isBrowsingBeyondAccess) return;
    previousBrowsingBeyondAccessRef.current = isBrowsingBeyondAccess;
    const store = useContentDiscoveryStore;
    store.getState().topicsModal.getFilteredSubjects(filtersRef.current, Date.now());
    if (store.getState().topicsModal.selectedSubject) {
      store.getState().topicsModal.getSubjectGuides(filtersRef.current, Date.now());
    }
  }, [isBrowsingBeyondAccess]);

  useEffect(() => {
    const store = useContentDiscoveryStore;

    const debouncedGetFilteredSubjects = debounce((contentDiscoveryFilters) => {
      store.getState().topicsModal.getFilteredSubjects(contentDiscoveryFilters, Date.now());
    }, 300);

    const unsubscribeSubjects = store.subscribe(
      (state) => ({
        areSubjectsPersonalized: state.topicsModal.areSubjectsPersonalized,
        subjectSearchString: state.topicsModal.subjectSearchString,
        subjectGuideSearchString: state.topicsModal.subjectGuideSearchString
      }),
      ({areSubjectsPersonalized, subjectSearchString, subjectGuideSearchString}, prevState) => {
        if (
          areSubjectsPersonalized !== prevState.areSubjectsPersonalized ||
          subjectSearchString !== prevState.subjectSearchString ||
          subjectGuideSearchString !== prevState.subjectGuideSearchString
        ) {
          debouncedGetFilteredSubjects.cancel();
          debouncedGetFilteredSubjects(filtersRef.current);
        }
      }
    );

    const debouncedGetSubjectGuides = debounce((contentDiscoveryFilters) => {
      store.getState().topicsModal.getSubjectGuides(contentDiscoveryFilters, Date.now());
    }, 300);

    const unsubscribeGuides = store.subscribe(
      (state) => ({
        selectedSubject: state.topicsModal.selectedSubject,
        subjectGuideSearchString: state.topicsModal.subjectGuideSearchString
      }),
      ({selectedSubject, subjectGuideSearchString}, prevState) => {
        if (
          selectedSubject !== prevState.selectedSubject ||
          subjectGuideSearchString !== prevState.subjectGuideSearchString
        ) {
          debouncedGetSubjectGuides.cancel();
          debouncedGetSubjectGuides(filtersRef.current);
        }
      }
    );

    const unsubscribeSelectedSubjectQuestionCounts = store.subscribe(
      (state) => ({selectedGuideLevels: state.topicsModal.selectedGuideLevels}),
      ({selectedGuideLevels}, prevState) => {
        if (!selectedGuideLevels.equals(prevState.selectedGuideLevels)) {
          store.getState().topicsModal.updateSelectedSubjects();
        }
      }
    );

    const unsubscribeUpdateSelectedSubjectGuides = store.subscribe(
      (state) => ({flattenedSubjectGuideLevels: state.topicsModal.flattenedSubjectGuideLevels}),
      ({flattenedSubjectGuideLevels}, prevState) => {
        if (!flattenedSubjectGuideLevels.equals(prevState.flattenedSubjectGuideLevels)) {
          store.getState().topicsModal.updateSelectedSubjectGuideLevels();
        }
      }
    );

    /* init ops */
    debouncedGetFilteredSubjects.cancel();
    debouncedGetFilteredSubjects(filtersRef.current);

    debouncedGetSubjectGuides.cancel();
    if (store.getState().topicsModal.selectedSubject) {
      store.getState().topicsModal.getSubjectGuides(filtersRef.current, Date.now());
    }

    if (
      !store
        .getState()
        .topicsModal.appliedGuideLevels.toSet()
        .equals(store.getState().topicsModal.selectedGuideLevels)
    ) {
      store.getState().topicsModal.resetSelectedGuideLevels();
    }

    if (store.getState().topicsModal.selectedGuideLevels.size > 0) {
      const selectedSubjectIds = store
        .getState()
        .topicsModal.selectedSubjects.reduce((acc, countObj) => {
          acc.push(countObj.id);
          return acc;
        }, []);
      store
        .getState()
        .topicsModal.getAndReplaceSelectedGuideLevels(
          selectedSubjectIds.join(','),
          filtersRef.current
        );
    }

    store.getState().topicsModal.updateSelectedSubjects();
    /* end init ops */

    return () => {
      unsubscribeSubjects();
      unsubscribeGuides();
      unsubscribeSelectedSubjectQuestionCounts();
      unsubscribeUpdateSelectedSubjectGuides();
      debouncedGetFilteredSubjects.cancel();
      debouncedGetSubjectGuides.cancel();
    };
  }, []);
};
